import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { confirmActivation } from '../api/configurationCheck'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  type PillTone,
  useConfirm,
  OverflowMenu,
  FormSection,
  FormGrid,
} from '../components/ui'
import {
  AUTOMATION_ACTION_LABELS,
  AUTOMATION_OPERATOR_LABELS,
  automationsApi,
  type AutomationAction,
  type AutomationActionKind,
  type AutomationAssignTo,
  type AutomationCondition,
  type AutomationConditionOperator,
  type AutomationEventType,
  type AutomationRule,
  type AutomationRuleStatus,
  type AutomationRun,
} from '../api/automations'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'

/**
 * F-062 (TASK-0316): automazioni interne. L'Admin sceglie un evento di business della pratica
 * (creata, inviata al backoffice, integrazione richiesta, approvata, attivata, rifiutata, cambio di
 * stato), le condizioni sugli attributi dell'evento e le azioni interne da eseguire (crea task,
 * notifica nel CRM, chiudi i task automatici della pratica). Nessun canale esterno. Una regola nasce
 * in Bozza e scatta solo da Attiva; una regola attiva non si modifica. Il registro mostra cosa e'
 * successo a ogni evento. Tutta la pagina e' per l'Admin (il server risponde 403 agli altri).
 */
const STATUS_TONE: Record<AutomationRuleStatus, PillTone> = {
  Bozza: 'neutral',
  Attiva: 'success',
  Archiviata: 'neutral',
  Test: 'info',
  Simulazione: 'info',
  Approvazione: 'warning',
  Pubblicata: 'primary',
}

const OUTCOME_TONE: Record<AutomationRun['outcome'], PillTone> = {
  Riuscita: 'success',
  Fallita: 'error',
  NonApplicabile: 'neutral',
}

const OPERATORS = Object.keys(AUTOMATION_OPERATOR_LABELS) as AutomationConditionOperator[]
const ACTION_KINDS = Object.keys(AUTOMATION_ACTION_LABELS) as AutomationActionKind[]

interface ActionForm {
  kind: AutomationActionKind
  title: string
  body: string
  assignTo: AutomationAssignTo
  assigneeUserId: string
  dueInHours: string
}

interface FormState {
  id?: number
  name: string
  eventType: string
  conditions: AutomationCondition[]
  actions: ActionForm[]
}

const EMPTY_ACTION: ActionForm = {
  kind: 'CreaTask',
  title: '',
  body: '',
  assignTo: 'agente',
  assigneeUserId: '',
  dueInHours: '',
}

function emptyForm(eventType: string): FormState {
  return { name: '', eventType, conditions: [], actions: [{ ...EMPTY_ACTION }] }
}

function toActionForm(a: AutomationAction): ActionForm {
  return {
    kind: a.kind,
    title: a.title ?? '',
    body: a.body ?? '',
    assignTo: a.assignTo ?? 'agente',
    assigneeUserId: a.assigneeUserId ? String(a.assigneeUserId) : '',
    dueInHours: a.dueInHours === null || a.dueInHours === undefined ? '' : String(a.dueInHours),
  }
}

function toAction(f: ActionForm): AutomationAction {
  if (f.kind === 'ChiudiTaskPratica') return { kind: f.kind }
  const action: AutomationAction = {
    kind: f.kind,
    title: f.title.trim(),
    body: f.body.trim() || null,
    assignTo: f.assignTo,
    assigneeUserId: f.assignTo === 'utente' ? Number.parseInt(f.assigneeUserId, 10) : null,
  }
  if (f.kind === 'CreaTask' && f.dueInHours.trim() !== '') action.dueInHours = Number.parseInt(f.dueInHours, 10)
  return action
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
}

export function AutomationRulesPage() {
  const askConfirm = useConfirm()
  const [rules, setRules] = useState<AutomationRule[]>([])
  const [eventTypes, setEventTypes] = useState<AutomationEventType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm(''))
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [runsRule, setRunsRule] = useState<AutomationRule | null>(null)
  const [runs, setRuns] = useState<AutomationRun[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [runsError, setRunsError] = useState<string | null>(null)

  const isEdit = form.id !== undefined
  const eventByCode = useMemo(() => new Map(eventTypes.map(t => [t.code, t])), [eventTypes])
  const currentEvent = eventByCode.get(form.eventType)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [types, list] = await Promise.all([automationsApi.eventTypes(), automationsApi.list()])
      setEventTypes(types)
      setRules(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // F-096: dopo "Clona" la copia (sempre in Bozza) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(rules, r => openEdit(r))

  function openCreate() {
    setForm(emptyForm(eventTypes[0]?.code ?? ''))
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(r: AutomationRule) {
    setForm({
      id: r.id,
      name: r.name,
      eventType: r.eventType,
      conditions: r.conditions.map(c => ({ ...c })),
      actions: r.actions.map(toActionForm),
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  function changeEvent(code: string) {
    const attrs = new Set(eventByCode.get(code)?.attributes.map(a => a.name) ?? [])
    // Le condizioni su attributi che il nuovo evento non ha vengono tolte.
    setForm(f => ({ ...f, eventType: code, conditions: f.conditions.filter(c => attrs.has(c.field)) }))
  }

  function addCondition() {
    const first = currentEvent?.attributes[0]?.name
    if (!first) return
    setForm(f => ({ ...f, conditions: [...f.conditions, { field: first, operator: 'eq', value: '' }] }))
  }

  function patchCondition(i: number, patch: Partial<AutomationCondition>) {
    setForm(f => ({ ...f, conditions: f.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }))
  }

  function patchAction(i: number, patch: Partial<ActionForm>) {
    setForm(f => ({ ...f, actions: f.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }))
  }

  async function handleSave() {
    if (!form.name.trim()) return setModalError('Il nome e\' obbligatorio')
    if (!form.eventType) return setModalError('Scegli un evento')
    if (form.conditions.some(c => !c.value.trim())) return setModalError('Ogni condizione ha bisogno di un valore')
    if (form.actions.length === 0) return setModalError('Serve almeno un\'azione')
    for (const a of form.actions) {
      if (a.kind === 'ChiudiTaskPratica') continue
      if (!a.title.trim()) return setModalError('Ogni azione ha bisogno di un titolo')
      if (a.assignTo === 'utente' && !(Number.parseInt(a.assigneeUserId, 10) > 0))
        return setModalError('Indica l\'id dell\'utente destinatario')
    }

    const body = {
      name: form.name.trim(),
      eventType: form.eventType,
      conditions: form.conditions.map(c => ({ ...c, value: c.value.trim() })),
      actions: form.actions.map(toAction),
    }
    setSaving(true)
    setModalError(null)
    try {
      if (isEdit) await automationsApi.update(form.id as number, body)
      else await automationsApi.create(body)
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function changeStatus(r: AutomationRule, status: AutomationRuleStatus) {
    setError(null)
    // F-122: prima di attivare si controllano i riferimenti; e' un avviso, non un blocco.
    if (status === 'Attiva' && !(await confirmActivation('AutomationRule', r.id, r.name))) return
    try {
      await automationsApi.setStatus(r.id, status)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'aggiornamento')
    }
  }

  async function handleDelete(r: AutomationRule) {
    if (!(await askConfirm({ title: 'Elimina bozza', message: `Eliminare la bozza "${r.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    setError(null)
    try {
      await automationsApi.remove(r.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'eliminazione')
    }
  }

  async function openRuns(r: AutomationRule) {
    setRunsRule(r)
    setRuns([])
    setRunsError(null)
    setRunsLoading(true)
    try {
      setRuns(await automationsApi.runs(r.id, 100))
    } catch (e) {
      setRunsError(e instanceof Error ? e.message : 'Errore nel caricamento del registro')
    } finally {
      setRunsLoading(false)
    }
  }

  function eventLabel(code: string): string {
    return eventByCode.get(code)?.label ?? code
  }

  function attributeLabel(eventType: string, field: string): string {
    return eventByCode.get(eventType)?.attributes.find(a => a.name === field)?.label ?? field
  }

  function conditionsText(r: AutomationRule): string {
    if (r.conditions.length === 0) return 'Sempre'
    return r.conditions
      .map(c => `${attributeLabel(r.eventType, c.field)} ${AUTOMATION_OPERATOR_LABELS[c.operator] ?? c.operator} ${c.value}`)
      .join(' e ')
  }

  function actionsText(r: AutomationRule): string {
    return r.actions.map(a => (a.title ? `${AUTOMATION_ACTION_LABELS[a.kind]}: ${a.title}` : AUTOMATION_ACTION_LABELS[a.kind])).join(' · ')
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Automazioni"
        icon="auto_mode"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate} disabled={eventTypes.length === 0}>
            Nuova regola
          </Button>
        }
      />

      <Callout tone="info">Quando succede un evento su una pratica e le condizioni sono vere, il CRM esegue in automatico le azioni interne scelte (crea un task, avvisa qualcuno nel CRM, chiude i task automatici). Nessun messaggio parte verso l'esterno. Una regola nuova e' in bozza: scatta solo dopo averla attivata.</Callout>

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {rules.length === 0 ? (
          <EmptyState icon="auto_mode" title="Nessuna regola configurata." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Evento</th>
                <th>Condizioni</th>
                <th>Azioni</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id}>
                  <td className="md-typescale-emphasized-body-medium">{r.name}</td>
                  <td>{eventLabel(r.eventType)}</td>
                  <td className="md-typescale-body-small">{conditionsText(r)}</td>
                  <td className="md-typescale-body-small">{actionsText(r)}</td>
                  <td><StatusPill tone={STATUS_TONE[r.status]}>{r.status}</StatusPill></td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <CloneConfigurationButton
                        entityLabel="regola di automazione"
                        url={`/api/automation-rules/${r.id}/clone`}
                        defaultName={r.name}
                        note="Si copiano evento, condizioni e azioni. Non si copiano esecuzioni e storico. La copia nasce sempre in Bozza."
                        onCloned={async id => { await load(); openAfterClone(id) }}
                      />
                      {r.status !== 'Attiva' && (
                        <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(r)} />
                      )}
                      <OverflowMenu
                        size="sm"
                        aria-label="Altre azioni"
                        items={[
                          ...(r.status === 'Bozza' || r.status === 'Pubblicata' ? [{ label: 'Attiva', icon: 'play_arrow', onSelect: () => void changeStatus(r, 'Attiva') }] : []),
                          ...(r.status === 'Attiva' ? [{ label: 'Riporta in bozza', icon: 'pause', onSelect: () => void changeStatus(r, 'Bozza') }] : []),
                          ...(r.status !== 'Archiviata' ? [{ label: 'Archivia', icon: 'archive', onSelect: () => void changeStatus(r, 'Archiviata') }] : []),
                          ...(r.status === 'Archiviata' ? [{ label: 'Riporta in bozza', icon: 'unarchive', onSelect: () => void changeStatus(r, 'Bozza') }] : []),
                          { label: 'Registro esecuzioni', icon: 'history', onSelect: () => void openRuns(r) },
                        ]}
                      />
                      {r.status === 'Bozza' && (
                        <IconButton icon="delete" size="sm" aria-label="Elimina bozza" onClick={() => void handleDelete(r)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <RuleLifecyclePanel family="automation" onChanged={() => void load()} />

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={isEdit ? 'Modifica regola' : 'Nuova regola'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}

          <FormSection title="Regola">
            <TextField
              id="ar-name"
              label="Nome *"
              type="text"
              maxLength={200}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              disabled={saving}
            />
            <Select
              id="ar-event"
              label="Quando succede *"
              value={form.eventType}
              onChange={e => changeEvent(e.target.value)}
              disabled={saving}
            >
              {eventTypes.map(t => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </Select>
          </FormSection>

          <FormSection title="Solo se (tutte le condizioni)" columns={1}>
            {form.conditions.length === 0 && <p className="text-muted md-typescale-body-small mb-0">Nessuna condizione: la regola scatta sempre.</p>}
            {form.conditions.map((c, i) => (
              <FormGrid key={i}>
                <Select
                  label="Attributo"
                  labelHidden
                  value={c.field}
                  onChange={e => patchCondition(i, { field: e.target.value })}
                  disabled={saving}
                >
                  {currentEvent?.attributes.map(a => (
                    <option key={a.name} value={a.name}>{a.label}</option>
                  ))}
                </Select>
                <Select
                  label="Operatore"
                  labelHidden
                  value={c.operator}
                  onChange={e => patchCondition(i, { operator: e.target.value as AutomationConditionOperator })}
                  disabled={saving}
                >
                  {OPERATORS.map(o => (
                    <option key={o} value={o}>{AUTOMATION_OPERATOR_LABELS[o]}</option>
                  ))}
                </Select>
                <div className="md-span-full d-flex gap-2 align-items-start">
                  <TextField
                    className="flex-grow-1"
                    label="Valore"
                    labelHidden
                    placeholder="Valore"
                    type="text"
                    maxLength={200}
                    value={c.value}
                    onChange={e => patchCondition(i, { value: e.target.value })}
                    disabled={saving}
                  />
                  <IconButton
                    icon="close"
                    size="sm"
                    aria-label="Togli condizione"
                    onClick={() => setForm(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }))}
                    disabled={saving}
                  />
                </div>
              </FormGrid>
            ))}
            <div>
              <Button variant="outlined" size="sm" onClick={addCondition} disabled={saving || form.conditions.length >= 10}>
                Aggiungi condizione
              </Button>
            </div>
          </FormSection>

          <FormSection title="Allora (azioni, in ordine)" columns={1}>
            {form.actions.map((a, i) => (
              <Card as="div" variant="filled" padding="sm" className="d-flex flex-column gap-2" key={i}>
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-grow-1">
                    <Select
                      label="Tipo di azione"
                      labelHidden
                      value={a.kind}
                      onChange={e => patchAction(i, { kind: e.target.value as AutomationActionKind })}
                      disabled={saving}
                    >
                      {ACTION_KINDS.map(k => (
                        <option key={k} value={k}>{AUTOMATION_ACTION_LABELS[k]}</option>
                      ))}
                    </Select>
                  </div>
                  <IconButton
                    icon="close"
                    size="sm"
                    aria-label="Togli azione"
                    onClick={() => setForm(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }))}
                    disabled={saving}
                  />
                </div>
                {a.kind !== 'ChiudiTaskPratica' && (
                  <>
                    <TextField
                      label={a.kind === 'CreaTask' ? 'Titolo del task *' : 'Titolo della notifica *'}
                      labelHidden
                      placeholder={a.kind === 'CreaTask' ? 'Titolo del task *' : 'Titolo della notifica *'}
                      type="text"
                      maxLength={200}
                      value={a.title}
                      onChange={e => patchAction(i, { title: e.target.value })}
                      disabled={saving}
                    />
                    <TextField
                      label="Testo (facoltativo, senza importi)"
                      labelHidden
                      placeholder="Testo (facoltativo, senza importi)"
                      multiline
                      rows={2}
                      maxLength={2000}
                      value={a.body}
                      onChange={e => patchAction(i, { body: e.target.value })}
                      disabled={saving}
                    />
                    <FormGrid>
                      <Select
                        label="Destinatario"
                        labelHidden
                        value={a.assignTo}
                        onChange={e => patchAction(i, { assignTo: e.target.value as AutomationAssignTo })}
                        disabled={saving}
                      >
                        <option value="agente">All&apos;agente della pratica</option>
                        <option value="utente">A un utente preciso</option>
                      </Select>
                      {a.assignTo === 'utente' && (
                        <TextField
                          label="Id utente"
                          labelHidden
                          placeholder="Id utente"
                          type="number"
                          min={1}
                          value={a.assigneeUserId}
                          onChange={e => patchAction(i, { assigneeUserId: e.target.value })}
                          disabled={saving}
                        />
                      )}
                      {a.kind === 'CreaTask' && (
                        <TextField
                          label="Scade dopo (ore)"
                          labelHidden
                          placeholder="Scade dopo (ore)"
                          type="number"
                          min={0}
                          value={a.dueInHours}
                          onChange={e => patchAction(i, { dueInHours: e.target.value })}
                          disabled={saving}
                        />
                      )}
                    </FormGrid>
                  </>
                )}
                {a.kind === 'ChiudiTaskPratica' && (
                  <p className="text-muted md-typescale-body-small mb-0">
                    Completa i task ancora aperti della pratica creati da un&apos;automazione. I task creati a mano
                    non vengono toccati.
                  </p>
                )}
              </Card>
            ))}
            <div>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => setForm(f => ({ ...f, actions: [...f.actions, { ...EMPTY_ACTION }] }))}
                disabled={saving || form.actions.length >= 10}
              >
                Aggiungi azione
              </Button>
            </div>
          </FormSection>
        </>
      </Dialog>

      <Dialog
        open={runsRule !== null}
        onClose={() => setRunsRule(null)}
        size="lg"
        title={`Registro esecuzioni — ${runsRule?.name ?? ''}`}
        actions={<Button variant="text" dismiss onClick={() => setRunsRule(null)}>Chiudi</Button>}
      >
        <>
          {runsError && <Callout tone="error">{runsError}</Callout>}
          {runsLoading ? (
            <LoadingState />
          ) : runs.length === 0 ? (
            <p className="text-muted mb-0">Questa regola non e&apos; ancora scattata su nessun evento.</p>
          ) : (
            <div className="md-dialog__bleed">
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Pratica</th>
                  <th>Esito</th>
                  <th>Azioni eseguite</th>
                  <th>Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run.id}>
                    <td>{formatDate(run.executedAt)}</td>
                    <td>
                      <Link to={`/contracts/${run.entityId}`} onClick={() => setRunsRule(null)}>
                        #{run.entityId}
                      </Link>
                    </td>
                    <td><StatusPill tone={OUTCOME_TONE[run.outcome]}>{run.outcome}</StatusPill></td>
                    <td className="md-num">{run.actionsExecuted}</td>
                    <td className="md-typescale-body-small text-muted">{run.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            </div>
          )}
        </>
      </Dialog>
    </Page>
  )
}
